import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { errorService } from "@servicios/errorService.service";
import { Router } from '@angular/router';
import { GlobalService } from '@servicios/_helpers/global.service';
import { AuthenticationService } from '@servicios/authentication.service';
import { environment } from 'environments/environment';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    constructor(private errorService: errorService, private router: Router, private gs: GlobalService, private auth: AuthenticationService) { }
    
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError(err => {
                console.log(err);
                
                if (!(err instanceof HttpErrorResponse)) {
                    return throwError(() => err);
                }

                // Caso 1: Mantenimiento / Backup (Code 2400)
                if (err.status === 401 && err.error?.code === '2400') {
                    this.router.navigate(['/backupPage']);
                    this.gs.cancelOngoingRequests();
                    return throwError(() => err);
                }

                // Caso 2: Token expirado o inválido (401 o Code 2401)
                if (err.status === 401 || err.error?.code === '2401') {
                    // Si la sesión ya expiró o no hay token activo, rechazar inmediatamente
                    // para evitar loops infinitos de peticiones concurrentes y bloqueos de navegador.
                    if (this.auth.sesionExpirada || !this.auth.getToken || this.auth.checkIdleTimeout?.()) {
                        return throwError(() => err);
                    }

                    const authOperations = ['Login', 'AppLogin'];
                    const isAuthEndpoint = request.url.includes('/api/auth/login') || 
                                           request.url.includes('/api/auth/refresh') || 
                                           request.url.includes('/api/auth/logout');
                    
                    // Si el error 401 viene de intentar loguearse o renovar, cerramos sesión directo
                    if (authOperations.includes(request.body?.operationName) || isAuthEndpoint) {
                        if (err.error?.code === 'EXPIRED_IDLE' || err.error?.error === 'EXPIRED_IDLE') {
                            this.auth.sesionExpirada = true;
                        }
                        this.auth.logout();
                        return throwError(() => err);
                    }

                    // Para peticiones normales, iniciamos el flujo de renovación
                    return this.handle401Error(request, next, err);
                }

                return throwError(() => err);
            })
        );
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler, err: any) {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            return this.auth.refreshToken().pipe(
                switchMap((token: any) => {
                    this.isRefreshing = false;
                    this.refreshTokenSubject.next(token);
                    return next.handle(this.addTokenHeader(request, token));
                }),
                catchError((refreshErr) => {
                    this.isRefreshing = false;
                    this.refreshTokenSubject.next(null);
                    if (refreshErr?.error?.code === 'EXPIRED_IDLE' || refreshErr?.error?.error === 'EXPIRED_IDLE') {
                        this.auth.sesionExpirada = true;
                    }
                    this.auth.logout();
                    return throwError(() => refreshErr);
                })
            );
        } else {
            return this.refreshTokenSubject.pipe(
                filter(token => token !== null),
                take(1),
                switchMap((token) => {
                    return next.handle(this.addTokenHeader(request, token));
                })
            );
        }
    }

    private addTokenHeader(request: HttpRequest<any>, token: string) {
        const isLimsBackend = (!request.url.startsWith('http://') && !request.url.startsWith('https://'))
            || (!!environment.apiUrl && request.url.startsWith(environment.apiUrl))
            || (!!environment.graphqlUrl && request.url.startsWith(environment.graphqlUrl));
        return request.clone({
            withCredentials: request.withCredentials || isLimsBackend,
            headers: request.headers.set('Authorization', "Bearer " + token)
        });
    }
}
